import type { Category } from '@/app/(pages)/sub-home/[category]/types';
import { CategoryType } from '@/app/(pages)/sub-home/[category]/types';
import { TARGET_TO_REGION_TYPE } from '@/app/_components/widgets/region-selector/recent-select-button/recent-select-modal/constants';

/** 로그 전송시에 사용되는 페이지 이름 */
export const SUBHOME_LOG_PAGE_NAME = {
  motel: 'MTHome',
  hotel: 'HTHome',
  pension: 'PSHome',
  guesthouse: 'GHHome',
  global: 'GHome',
  transportation: 'TransportationHome',
  residence: 'ResidenceHome',
  live: 'LiveCommerceHome',
  entertainment: 'EntertainmentHome',
  leisure: 'LeisureHome',
  kids: 'FamilyHome',
} as const;
export type SubhomeLogPageNameKey = keyof typeof SUBHOME_LOG_PAGE_NAME;
export type SubhomeLogPageNameValue = (typeof SUBHOME_LOG_PAGE_NAME)[SubhomeLogPageNameKey];
export type HomeLogPageNameValue = SubhomeLogPageNameValue | 'Home';

/** 서브홈의 각 지면별 타입 (통합 호출 시 사용) */
export const SUB_HOME_TARGET_TYPE = {
  hotel: 'DOMESTIC_HOTEL',
  pension: 'DOMESTIC_PENSION',
  residence: 'RESIDENCE',
  motel: 'DOMESTIC_MOTEL',
  guesthouse: 'DOMESTIC_GUESTHOUSE',
  global: 'OVERSEA',
  transportation: 'TRANSPORTATION',
  leisure: 'LEISURE',
  kids: 'KIDYANOLJA',
  entertainment: 'ENTERTAINMENT',
  live: 'LIVE',
} as const;

export const getSubHomeTargetType = (target: HomeLogPageNameValue): string => {
  // SUBHOME_LOG_PAGE_NAME의 값에서 해당 키를 찾음
  // eslint-disable-next-line @typescript-eslint/non-nullable-type-assertion-style
  const key = (Object.keys(SUBHOME_LOG_PAGE_NAME) as SubhomeLogPageNameKey[]).find(
    (subHomeKey) => SUBHOME_LOG_PAGE_NAME[subHomeKey] === target,
  ) as SubhomeLogPageNameKey;

  // 찾은 키를 기반으로 SUB_HOME_TARGET_TYPE 값을 반환
  return SUB_HOME_TARGET_TYPE[key] || TARGET_TO_REGION_TYPE.DOMESTIC_MOTEL;
};

/** 서브홈 타입 별 검색 탭 이동 경로 */
const BASE_SEARCH_URL = '/search?tab=';
export const SUB_HOME_TYPE_TO_SEARCH_URL: Record<Category, string> = {
  [CategoryType.motel]: `${BASE_SEARCH_URL}place`,
  [CategoryType.hotel]: `${BASE_SEARCH_URL}place`,
  [CategoryType.pension]: `${BASE_SEARCH_URL}place`,
  [CategoryType.guesthouse]: `${BASE_SEARCH_URL}place`,
  [CategoryType.global]: `${BASE_SEARCH_URL}gplace`,
  [CategoryType.transportation]: `${BASE_SEARCH_URL}place`,
  [CategoryType.residence]: `${BASE_SEARCH_URL}place`,
  [CategoryType.live]: `${BASE_SEARCH_URL}place`,
  [CategoryType.entertainment]: `${BASE_SEARCH_URL}entertainment`,
  [CategoryType.leisure]: `${BASE_SEARCH_URL}leisure`,
  [CategoryType.kids]: `${BASE_SEARCH_URL}leisure`,
};

/**
 * 서브홈 타입 별 딥링크
 * @see https://yanolja.atlassian.net/wiki/spaces/APPDEV/pages/51511787
 */
export const SUB_HOME_TYPE_TO_DEEP_LINK = {
  [CategoryType.motel]: 'motel',
  [CategoryType.hotel]: 'hotel',
  [CategoryType.pension]: 'pension',
  [CategoryType.guesthouse]: 'guestHouse',
  [CategoryType.global]: 'gplace',
  [CategoryType.transportation]: 'transportation',
  [CategoryType.residence]: 'residence',
  [CategoryType.live]: 'live',
  [CategoryType.entertainment]: 'entertainment',
  [CategoryType.leisure]: 'leisure',
  [CategoryType.kids]: 'family',
};

export const SEO_META = {
  [CategoryType.hotel]: {
    title: '프리미엄부터 가성비 호텔까지, 특가&할인혜택! 놀수록 놀라운 세상 | NOL',
    description:
      '비즈니스호텔부터 5성급 호텔까지 다채로운 호텔 및 리조트를 편리하게 검색하고, 쿠폰혜택으로 특가 호텔을 쉽고 빠르게 예약하세요.',
    tagKeywords:
      '숙박예약, 숙소예약, 호텔쿠폰, 호텔할인, 호텔놀수록 놀라운 혜택!, 리조트검색, NOL,놀, nol, 야놀자, yanolja',
  },
  [CategoryType.pension]: {
    title: '인기 펜션부터 풀빌라, 글램핑까지, 특가&할인혜택! 놀수록 놀라운 세상 | NOL',
    description:
      '가족, 친구들과 함께 갈 펜션, 풀빌라를 편리하게 검색하고, 다양한 쿠폰혜택으로 특가 펜션을 쉽고 빠르게 예약하세요.',
    tagKeywords:
      '펜션예약, 풀빌라예약, NOL, 펜션놀수록 놀라운 혜택!, 단체펜션, 독채펜션, 풀빌라찾기, 풀빌라 펜션,놀, nol, 야놀자, yanolja',
  },
  [CategoryType.residence]: {
    title: '가족과 함께하기 좋은 숙소, 특가&할인혜택! 놀수록 놀라운 세상 | NOL',
    description:
      '가족과 함께 할 다인실 숙소부터 리조트, 키즈펜션까지 편리하게 검색하고 할인 쿠폰으로 합리적으로 예약하세요.',
    tagKeywords:
      '가족형숙소, 생활형숙박, 레지던스, NOL, 놀수록 놀라운 혜택!, 주방이있는 숙소, 다인실숙소, 리조트, 키즈펜션, 연박할인,놀, nol, 야놀자, yanolja',
  },
  [CategoryType.motel]: {
    title: '내 주변 할인 모텔부터 파티룸, 스파, 프리미엄 모텔까지 놀수록 놀라운 세상 | NOL',
    description:
      '내 주변 특가 모텔부터 파티룸, 프리미엄 모텔까지 편리하게 검색하고 할인 쿠폰으로 합리적으로 예약하세요.',
    tagKeywords:
      '모텔, 모텔예약, 모텔할인, 대실, 대실할인, 무한대실, 놀아보고서, 좋은숙박, 혼자, 배낭, 둘이, 연인, 가족, 여행, 드라이브, 추천, 인기, 놀수록 놀라운 혜택!, 저렴, 쿠폰,놀, nol, 야놀자, yanolja',
  },
  [CategoryType.leisure]: {
    title: '레저&액티비티 예약 놀라운 세상 | NOL',
    description: '국내 인기 국내레저를 편리하게 검색하고, 놀라운 혜택으로 합리적으로 예약하세요.',
    tagKeywords:
      '놀아보고서, 좋은숙박, 혼자, 배낭, 둘이, 연인, 가족, 여행, 드라이브, 추천, 인기, 놀수록 놀라운 혜택!, 저렴, 쿠폰,놀, nol, 야놀자, yanolja',
  },
  [CategoryType.global]: {
    title: '해외숙소 특가&할인혜택! 놀라운 세상 | NOL',
    description: '전세계 호텔 및 리조트를 편리하게 검색하고, 쿠폰혜택으로 해외숙소를 쉽고 빠르게 예약하세요',
    tagKeywords: '해외여행, 해외숙소, 여행자보험, 해외호텔, 해외호텔추천, 해외여행추천, NOL,놀, nol, 야놀자, yanolja',
  },
  [CategoryType.transportation]: {
    title: '항공권은 물론, 기차&버스&렌터카 예약까지 놀라운 세상 | NOL',
    description: '항공, 기차, 버스, 렌터카까지 NOL에서 함께하세요.',
    tagKeywords:
      '항공권예약, 기차예약, 고속버스예약, 렌터카할인, 항공놀수록 놀라운 혜택!, 비행기표, 해외여행,놀, nol, 야놀자, yanolja',
  },
  [CategoryType.kids]: {
    title: '아이과 함께하는 놀수록 놀라운 세상 | NOL',
    description:
      '아이과 함께 즐길 수 있는 키즈카페, 놀이동산, 아쿠아리움, 동물원까지 놀라운 혜택으로 합리적으로 예약하세요.',
    tagKeywords:
      '키즈카페, 놀이동산, 아쿠아리움, 키즈체험, 동물원, 가족 나들이, 어린이 활동, 아이들과 함께, 어린이 체험, 가족 여행, 어린이 놀이공간, 키즈 액티비티, 어린이 이벤트, 가족 이벤트, 어린이 공원, 실내 놀이공간, 야놀자, yanolja, 놀, nol',
  },
  [CategoryType.entertainment]: {
    title: '보고싶던 공연&전시도 NOL에 다! 놀수록 놀라운 세상 | NOL',
    description: '뮤지컬, 콘서트, 연극, 전시 예매도 NOL에서 함께하세요.',
    tagKeywords:
      '공연, 전시, 뮤지컬, 콘서트, 연극, 전시, 클래식, 무용, 주말놀거리, 주말볼거리, 놀, nol, 야놀자, yanolja',
  },
  [CategoryType.live]: {
    title: '놓칠 수 없는 NOL 라이브 특가 놀라운 세상 | NOL',
    description: 'NOL 라이브로 검색하고 최저가로 예약하세요',
    tagKeywords: '놀, nol, 야놀자, yanolja',
  },
};
